import { useT } from '@/core/I18nProvider';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { HowItWorks } from './how/HowItWorks';
import { ClosingCta } from './sections/ClosingCta';
import { Control } from './sections/Control';
import { Hero } from './sections/Hero';
import { Reach } from './sections/Reach';
import { Story } from './story/Story';
import { LANDING_FAQ, TrustObjections } from './trust';

import type { ReactElement } from 'react';

/**
 * The marketing home (`/`) — a port of the "Syncanix Landing.dc.html" Claude
 * Design (see `apps/marketing/docs/marketing-redesign.md`).
 *
 * ## What changed, and why it matters structurally
 *
 * The page is now LIGHT, and it carries the site-wide chrome instead of its
 * own. Previously the home route was a dark island with a bespoke nav and
 * footer, and `App.tsx` suppressed the shared header/footer for it alone — so
 * the site had two chromes to keep in step and a visitor crossed a hard visual
 * seam on their first click. One ground, one chrome, one design system: the
 * landing is now the same site as `/pricing`, just with more to show.
 *
 * ## The band stack
 *
 * hero → story → how it works → where it works → control → objections →
 * closing CTA.
 *
 * Two of those are demos and they are deliberately adjacent: the story band
 * proves the agent BUILDS ITSELF, and how-it-works proves what that costs the
 * customer (one line, then nothing). Everything after them is the argument in
 * prose, which is why `Reach` is the plainest band on the page — it is the
 * first rest after roughly forty seconds of motion.
 *
 * `TrustObjections` is last before the close because it is where a reader who
 * is nearly convinced goes to be talked out of it. It is also the band that
 * makes the page's own structured data true: the `faq` payload below declares
 * six questions to search engines, and `FAQPage` requires them to be visible on
 * the page that declares them.
 */
export function LandingPage(): ReactElement {
  const { t } = useT();

  useLocalizedSeoMeta({
    title: t('home.meta.title'),
    fullTitle: t('home.meta.fullTitle'),
    description: t('home.meta.description'),
    path: '/',
    faq: LANDING_FAQ.map((entry) => ({
      question: t(entry.questionKey),
      answer: t(entry.answerKey),
    })),
  });

  return (
    /*
     * `overflow-x: clip` — the hero's mark is deliberately wider than the
     * viewport on a phone (620px of light against a 390px screen), and without
     * this it is real document overflow: measured 608px of scroll width against
     * 422px of client width, i.e. a page that slides sideways under the thumb.
     *
     * `clip` and not `hidden`: `hidden` would make this a scroll container,
     * which per spec forces `overflow-y` to `auto` — that would cut the mark at
     * the band's top and bottom edges, where the whole point is that it bleeds
     * out of them softly. `clip` clips on one axis and leaves the other
     * genuinely visible.
     *
     * It is here rather than on the hero because the clip has to happen at the
     * VIEWPORT's width: the hero band is `max-w-[1000px]`, so clipping there
     * would cut the mark at 500px from centre — inside its own fade, which puts
     * a hard vertical edge back into a layer whose whole job is not to have one.
     */
    <main id="main" tabIndex={-1} data-screen-label="home" className="[overflow-x:clip]">
      <Hero />
      <Story />
      <HowItWorks />
      <Reach />
      <Control />
      <TrustObjections />
      <ClosingCta />
    </main>
  );
}
